import { Injectable } from '@angular/core';
import { Course } from '@app/app/page-modules/course/models/course.model';
import { ObservableResult } from '@app/app/shared/store';
import { delay } from 'rxjs/operators';
import {
  CourseCreationRequest,
  CourseDataService,
  CourseSectionRequestUnion,
  CourseSectionResponse,
  CourseUpdateRequest,
  CreateCourseResponse,
  UpdateCourseResponse,
} from './data.service';

@Injectable()
export class MockCourseDataService implements CourseDataService {
  private courses: Course[] = [
    {
      _id: '1',
      title: 'Introduction to Angular',
      uri: 'intro-to-angular',
      formattedUri: 'intro-to-angular',
      header: 'Introduction to Angular',
      subHeader: 'Learn the fundamentals of Angular framework.',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: 'main-1',
        title: 'Introduction to Angular',
        content: [
          {
            type: 'PARAGRAPH',
            content: 'Welcome to Angular fundamentals course.',
          },
        ],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [
        {
          cards: [],
          cardsLayoutType: 'CardView',
          uid: 'section-1',
          title: 'Core Concepts',
          content: [
            {
              type: 'PARAGRAPH',
              content: 'Angular core concepts and fundamentals.',
            },
          ],
          layoutType: 'NO_BANNER',
        },
      ],
      authorDetails: {
        uid: 'author-1',
        name: 'Angular Team',
        organization: 'Google',
        displayConfig: {
          authorName: true,
          organizationName: true,
        },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: false,
        canPublish: false,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: '2023-12-01T10:30:00Z',
      status: 'PUBLISHED',
      tags: [
        { title: 'Angular', _id: 'tag-1' },
        { title: 'Frontend', _id: 'tag-2' },
      ],
      categories: [{ title: 'Web Development', frameworkId: 'web-framework' }],
      completed: false,
      completedOn: null,
      likes: {
        totalLikes: 156,
        vote: false,
      },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 120,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 8,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    },
    {
      _id: '2',
      title: 'React Fundamentals',
      uri: 'react-fundamentals',
      formattedUri: 'react-fundamentals',
      header: 'React Fundamentals',
      subHeader: 'Master the basics of React library.',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: 'main-2',
        title: 'React Fundamentals',
        content: [
          {
            type: 'PARAGRAPH',
            content: 'Learn React from the ground up.',
          },
        ],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [],
      authorDetails: {
        uid: 'author-2',
        name: 'React Team',
        organization: 'Meta',
        displayConfig: {
          authorName: true,
          organizationName: true,
        },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: false,
        canPublish: false,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: '2023-12-01T10:30:00Z',
      status: 'PUBLISHED',
      tags: [
        { title: 'React', _id: 'tag-3' },
        { title: 'JavaScript', _id: 'tag-4' },
      ],
      categories: [{ title: 'Web Development', frameworkId: 'web-framework' }],
      completed: false,
      completedOn: null,
      likes: {
        totalLikes: 203,
        vote: false,
      },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 90,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 6,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    },
    {
      _id: '3',
      title: 'TypeScript Essentials',
      uri: 'typescript-essentials',
      formattedUri: 'typescript-essentials',
      header: 'TypeScript Essentials',
      subHeader: 'Learn TypeScript for modern web development.',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: 'main-3',
        title: 'TypeScript Essentials',
        content: [
          {
            type: 'PARAGRAPH',
            content: 'Master TypeScript for better JavaScript development.',
          },
        ],
        layoutType: 'TOP_BANNER',
      },
      standardSections: [],
      authorDetails: {
        uid: 'author-3',
        name: 'TypeScript Team',
        organization: 'Microsoft',
        displayConfig: {
          authorName: true,
          organizationName: true,
        },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: false,
        canPublish: false,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: '2023-12-01T10:30:00Z',
      status: 'PUBLISHED',
      tags: [
        { title: 'TypeScript', _id: 'tag-5' },
        { title: 'Programming', _id: 'tag-6' },
      ],
      categories: [{ title: 'Programming Languages', frameworkId: 'lang-framework' }],
      completed: false,
      completedOn: null,
      likes: {
        totalLikes: 89,
        vote: false,
      },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 75,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 5,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    },
  ];

  getCourse(courseUri: string, languageCode?: string): ObservableResult<Course> {
    const course = this.courses.find((c) => c.uri === courseUri);
    if (!course) {
      return ObservableResult.ofError(`Course with uri ${courseUri} not found`).pipe(delay(300));
    }
    return ObservableResult.ofSuccess(course).pipe(delay(300));
  }

  createCourse(request: CourseCreationRequest): ObservableResult<CreateCourseResponse> {
    const newCourse: Course = {
      _id: `course-${Date.now()}`,
      title: request.mainSection.title || 'Untitled Course',
      uri: `course-${Date.now()}`,
      formattedUri: `course-${Date.now()}`,
      header: request.mainSection.title || 'Untitled Course',
      subHeader: '',
      mainSection: {
        cards: [],
        cardsLayoutType: 'CardView',
        uid: `main-${Date.now()}`,
        title: request.mainSection.title || '',
        content: request.mainSection.content || [],
        layoutType: request.mainSection.layoutType,
      },
      standardSections: [],
      authorDetails: {
        uid: 'mock-author',
        name: 'Mock Author',
        organization: 'Mock Organization',
        displayConfig: { authorName: true, organizationName: true },
        orgLogoUrl: '',
        userImageUrl: '',
      },
      permission: {
        canEdit: true,
        canPublish: true,
        canViewAnalytics: false,
        canDoMemberExports: false,
      },
      lastUpdatedOn: new Date().toISOString(),
      status: 'NEW',
      tags: [],
      categories: [],
      completed: false,
      completedOn: null,
      likes: { totalLikes: 0, vote: false },
      favorite: false,
      type: 'INSTITUTIONAL_LIBRARY',
      timeRequired: 0,
      availableLanguages: [{ code: 'en', name: 'English', enabled: true }],
      adminRights: [],
      embedded: false,
      completedCardsCount: 0,
      totalCardsCount: 0,
      dataForms: [],
      userPlaylistSubmissionSummary: undefined,
      draft: undefined,
    };

    this.courses.push(newCourse);

    return ObservableResult.ofSuccess({
      item: newCourse,
      uploadUrl: null,
    } as CreateCourseResponse).pipe(delay(300));
  }

  updateCourse(courseUid: string, request: CourseUpdateRequest, languageCode?: string): ObservableResult<UpdateCourseResponse> {
    const course = this.courses.find((c) => c._id === courseUid);
    if (course) {
      course.mainSection = {
        ...course.mainSection,
        title: request.mainSection.title,
        content: request.mainSection.content || [],
        layoutType: request.mainSection.layoutType,
      };
    }
    return ObservableResult.ofSuccess({
      uri: course?.uri || courseUid,
      formattedUri: course?.formattedUri || courseUid,
      coverImageUploadUrl: null,
    }).pipe(delay(300));
  }

  addCourseSection(courseUid: string, request: CourseSectionRequestUnion, languageCode?: string): ObservableResult<CourseSectionResponse> {
    const response: CourseSectionResponse = {
      uid: `section-${Date.now()}`,
    };
    return ObservableResult.ofSuccess(response).pipe(delay(300));
  }

  updateCourseSection(
    courseUid: string,
    sectionUid: string,
    request: CourseSectionRequestUnion,
    languageCode?: string,
  ): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  deleteCourseSection(courseUid: string, sectionUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  reorderCourseSections(courseUid: string, request: { sections: string[] }): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  publishCourse(courseUid: string): ObservableResult<void> {
    const course = this.courses.find((c) => c._id === courseUid);
    if (course) {
      course.status = 'PUBLISHED';
    }
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  unpublishCourse(courseUid: string): ObservableResult<void> {
    const course = this.courses.find((c) => c._id === courseUid);
    if (course) {
      course.status = 'UNPUBLISHED';
    }
    return ObservableResult.ofSuccess().pipe(delay(300));
  }

  archiveCourse(courseUid: string): ObservableResult<void> {
    return ObservableResult.ofSuccess().pipe(delay(300));
  }
}
